import React, { useEffect, useState } from "react";
import axios from "axios";
import { BoltIcon } from "../components/Bolts";
import AdminNav from "../components/AdminNav";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../components/ui/tabs";
import { Mail, MessageSquare, RefreshCw, Inbox, Sparkles, Tag, CreditCard, Package, GitBranch, TrendingUp, Sticker } from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => {
    try {
        return new Date(d).toLocaleString();
    } catch {
        return d;
    }
};

const AdminPage = () => {
    const [quotes, setQuotes] = useState([]);
    const [orders, setOrders] = useState([]);
    const [requests, setRequests] = useState([]);
    const [promoCodes, setPromoCodes] = useState([]);
    const [stats, setStats] = useState({ quotes: 0, contacts: 0 });
    const [traffic, setTraffic] = useState({ total_views: 0, last_7_days: [], views_last_7_days: 0, top_pages: [] });
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [q, s, o, r, t, pc] = await Promise.all([
                axios.get(`${API}/quotes`),
                axios.get(`${API}/admin/stats`),
                axios.get(`${API}/orders`),
                axios.get(`${API}/custom-requests`),
                axios.get(`${API}/admin/pageview-stats`),
                axios.get(`${API}/promo-codes`),
            ]);
            setQuotes(q.data || []);
            setStats(s.data || { quotes: 0, contacts: 0 });
            setOrders(o.data || []);
            setRequests(r.data || []);
            setTraffic(t.data || { total_views: 0, last_7_days: [], views_last_7_days: 0, top_pages: [] });
            setPromoCodes(pc.data || []);
        } catch (e) {
            // Silent fail; show empty
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <div data-testid="admin-page" className="min-h-[80vh]">
            <section className="border-b border-white/10 bg-storm-navy">
                <div className="container-x py-16 md:py-20 flex items-end justify-between flex-wrap gap-6">
                    <div>
                        <p className="eyebrow flex items-center gap-2 mb-4">
                            <BoltIcon className="w-3.5 h-3.5" /> Admin
                        </p>
                        <h1 className="text-4xl md:text-6xl text-white leading-[0.9]">
                            Submissions <span className="text-storm-bolt">Dashboard</span>
                        </h1>
                        <p className="text-storm-mist mt-3 max-w-xl">
                            Internal view of orders, quote requests, and custom requests.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        className="btn-secondary !text-sm !px-5 !py-3"
                        data-testid="admin-refresh-btn"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>
            </section>

            <AdminNav active="dashboard" />

            <section className="container-x pt-10">
                <div className="grid md:grid-cols-3 gap-5">
                    <Link
                        to="/admin/photo-studio"
                        data-testid="open-photo-studio"
                        className="surface-solid p-6 md:p-7 flex items-center justify-between gap-6 group hover:border-storm-bolt/40 transition-colors"
                    >
                        <div className="flex items-center gap-5">
                            <span className="inline-flex items-center justify-center w-12 h-12 bg-storm-bolt text-black">
                                <Sparkles className="w-5 h-5" />
                            </span>
                            <div>
                                <div className="font-heading text-xl md:text-2xl text-white">
                                    Photo Studio
                                </div>
                                <div className="text-sm text-storm-mist mt-1">
                                    Enhance photos, place shirts on AI models, manage listings.
                                </div>
                            </div>
                        </div>
                        <span className="text-xs uppercase tracking-[0.25em] text-storm-bolt group-hover:translate-x-1 transition-transform">
                            Open →
                        </span>
                    </Link>
                    <Link
                        to="/admin/pricing"
                        data-testid="open-pricing"
                        className="surface-solid p-6 md:p-7 flex items-center justify-between gap-6 group hover:border-storm-bolt/40 transition-colors"
                    >
                        <div className="flex items-center gap-5">
                            <span className="inline-flex items-center justify-center w-12 h-12 bg-storm-bolt text-black">
                                <Tag className="w-5 h-5" />
                            </span>
                            <div>
                                <div className="font-heading text-xl md:text-2xl text-white">
                                    Category Pricing
                                </div>
                                <div className="text-sm text-storm-mist mt-1">
                                    Set one default price per sub-category — applies to every listing in it.
                                </div>
                            </div>
                        </div>
                        <span className="text-xs uppercase tracking-[0.25em] text-storm-bolt group-hover:translate-x-1 transition-transform">
                            Open →
                        </span>
                    </Link>
                    <Link
                        to="/admin/boneyard"
                        data-testid="open-boneyard"
                        className="surface-solid p-6 md:p-7 flex items-center justify-between gap-6 group hover:border-storm-bolt/40 transition-colors"
                    >
                        <div className="flex items-center gap-5">
                            <span className="inline-flex items-center justify-center w-12 h-12 bg-storm-bolt text-black">
                                <GitBranch className="w-5 h-5" />
                            </span>
                            <div>
                                <div className="font-heading text-xl md:text-2xl text-white">
                                    The Boneyard
                                </div>
                                <div className="text-sm text-storm-mist mt-1">
                                    Categories, subcategories & themes — tile photos, hide/unhide, add new.
                                </div>
                            </div>
                        </div>
                        <span className="text-xs uppercase tracking-[0.25em] text-storm-bolt group-hover:translate-x-1 transition-transform">
                            Open →
                        </span>
                    </Link>
                    <Link
                        to="/admin/sticky-wicket"
                        data-testid="open-sticky-wicket"
                        className="surface-solid p-6 md:p-7 flex items-center justify-between gap-6 group hover:border-storm-bolt/40 transition-colors"
                    >
                        <div className="flex items-center gap-5">
                            <span className="inline-flex items-center justify-center w-12 h-12 bg-storm-bolt text-black">
                                <Sticker className="w-5 h-5" />
                            </span>
                            <div>
                                <div className="font-heading text-xl md:text-2xl text-white">
                                    The Sticky Wicket
                                </div>
                                <div className="text-sm text-storm-mist mt-1">
                                    Upload sticker blind-bags & vinyl decals — set sizes, prices, colors.
                                </div>
                            </div>
                        </div>
                        <span className="text-xs uppercase tracking-[0.25em] text-storm-bolt group-hover:translate-x-1 transition-transform">
                            Open →
                        </span>
                    </Link>
                </div>
            </section>

            <section className="container-x pt-10">
                <TrafficPanel traffic={traffic} />
            </section>

            <section className="container-x py-12 md:py-16">
                <div className="grid grid-cols-3 gap-5 mb-10">
                    <StatBlock
                        icon={<Inbox className="w-5 h-5" />}
                        label="Quote Requests"
                        value={stats.quotes}
                        testid="stat-quotes"
                    />
                    <StatBlock
                        icon={<Package className="w-5 h-5" />}
                        label="Custom Requests"
                        value={requests.length}
                        testid="stat-requests"
                    />
                    <StatBlock
                        icon={<CreditCard className="w-5 h-5" />}
                        label="Paid Orders"
                        value={orders.filter((o) => o.status === "paid").length}
                        testid="stat-orders"
                    />
                </div>

                <Tabs defaultValue="quotes" className="w-full">
                    <TabsList className="bg-storm-navy border border-white/10 rounded-none p-0 h-auto gap-0">
                        <TabsTrigger
                            value="quotes"
                            data-testid="tab-quotes"
                            className="font-heading uppercase tracking-wider text-sm rounded-none data-[state=active]:bg-storm-bolt data-[state=active]:text-black px-6 py-3 text-white/70"
                        >
                            Quotes ({quotes.length})
                        </TabsTrigger>
                        <TabsTrigger
                            value="orders"
                            data-testid="tab-orders"
                            className="font-heading uppercase tracking-wider text-sm rounded-none data-[state=active]:bg-storm-bolt data-[state=active]:text-black px-6 py-3 text-white/70"
                        >
                            Orders ({orders.length})
                        </TabsTrigger>
                        <TabsTrigger
                            value="requests"
                            data-testid="tab-requests"
                            className="font-heading uppercase tracking-wider text-sm rounded-none data-[state=active]:bg-storm-bolt data-[state=active]:text-black px-6 py-3 text-white/70"
                        >
                            Requests ({requests.length})
                        </TabsTrigger>
                        <TabsTrigger
                            value="promos"
                            data-testid="tab-promos"
                            className="font-heading uppercase tracking-wider text-sm rounded-none data-[state=active]:bg-storm-bolt data-[state=active]:text-black px-6 py-3 text-white/70"
                        >
                            Promo Codes ({promoCodes.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="quotes" className="mt-6">
                        <div className="surface-solid overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-white/10 hover:bg-transparent">
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">When</TableHead>
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">Name</TableHead>
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">Email</TableHead>
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">Category</TableHead>
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">Description</TableHead>
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow><TableCell colSpan={6} className="text-center text-storm-mist py-10">Loading…</TableCell></TableRow>
                                    ) : quotes.length === 0 ? (
                                        <TableRow><TableCell colSpan={6} className="text-center text-storm-mist py-10">No quotes yet.</TableCell></TableRow>
                                    ) : (
                                        quotes.map((q) => (
                                            <TableRow key={q.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`quote-row-${q.id}`}>
                                                <TableCell className="text-storm-mist text-xs whitespace-nowrap">{fmtDate(q.created_at)}</TableCell>
                                                <TableCell className="text-white font-medium">{q.name}</TableCell>
                                                <TableCell className="text-white/90">
                                                    <a href={`mailto:${q.email}`} className="hover:text-storm-bolt inline-flex items-center gap-1">
                                                        <Mail className="w-3.5 h-3.5" /> {q.email}
                                                    </a>
                                                </TableCell>
                                                <TableCell className="text-white/90">
                                                    {q.category}{q.subcategory ? ` · ${q.subcategory}` : ""}
                                                </TableCell>
                                                <TableCell className="text-storm-mist max-w-md">
                                                    <div className="line-clamp-2">{q.description}</div>
                                                </TableCell>
                                                <TableCell>
                                                    {q.email_sent ? (
                                                        <span className="text-storm-bolt text-xs">Sent</span>
                                                    ) : (
                                                        <span className="text-storm-mist text-xs">Stored</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="orders" className="mt-6">
                        <div className="surface-solid overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-white/10 hover:bg-transparent">
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">When</TableHead>
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">Customer & Ship-To</TableHead>
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">Items</TableHead>
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">Total</TableHead>
                                        <TableHead className="text-storm-bolt uppercase tracking-wider text-xs">Status / Shipping</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow><TableCell colSpan={5} className="text-center text-storm-mist py-10">Loading…</TableCell></TableRow>
                                    ) : orders.length === 0 ? (
                                        <TableRow><TableCell colSpan={5} className="text-center text-storm-mist py-10">No orders yet.</TableCell></TableRow>
                                    ) : (
                                        orders.map((o) => (
                                            <OrderRow key={o.id} order={o} onShipped={load} />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                    <TabsContent value="requests" className="mt-6">
                        <div className="space-y-4">
                            {loading ? (
                                <p className="text-center text-storm-mist py-10">Loading…</p>
                            ) : requests.length === 0 ? (
                                <p className="text-center text-storm-mist py-10">No custom requests yet.</p>
                            ) : (
                                requests.map((r) => (
                                    <div key={r.id} className="surface-solid p-5" data-testid={`request-row-${r.id}`}>
                                        <div className="flex flex-wrap justify-between gap-3 mb-3">
                                            <div>
                                                <span className="text-white font-medium text-lg">{r.name}</span>
                                                <span className="text-storm-mist text-xs ml-3">{fmtDate(r.created_at)}</span>
                                            </div>
                                            <div className="text-xs text-storm-mist text-right">
                                                {r.email && <a href={`mailto:${r.email}`} className="hover:text-storm-bolt block">{r.email}</a>}
                                                {r.phone && <span className="block">{r.phone}</span>}
                                            </div>
                                        </div>
                                        {r.address && <p className="text-storm-mist text-sm mb-2 whitespace-pre-line">{r.address}</p>}
                                        {(r.length_in || r.width_in) && (
                                            <p className="text-storm-bolt text-xs uppercase tracking-wider mb-2">
                                                Size: {[r.length_in && `${r.length_in}in L`, r.width_in && `${r.width_in}in W`].filter(Boolean).join(" × ")}
                                            </p>
                                        )}
                                        <p className="text-white/90 text-sm whitespace-pre-wrap mb-3">{r.description}</p>
                                        {(r.image_ids || []).length > 0 && (
                                            <div className="flex flex-wrap gap-3">
                                                {r.image_ids.map((imgId) => (
                                                    <a key={imgId} href={`${API}/custom-requests/image/${imgId}`} target="_blank" rel="noreferrer"
                                                        className="block w-24 h-24 border border-white/10 hover:border-storm-bolt">
                                                        <img src={`${API}/custom-requests/image/${imgId}`} alt="" className="w-full h-full object-cover" />
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="promos" className="mt-6">
                        <PromoCodesPanel promoCodes={promoCodes} reload={load} />
                    </TabsContent>
                </Tabs>
            </section>
        </div>
    );
};


const OrderRow = ({ order: o, onShipped }) => {
    const [tracking, setTracking] = useState("");
    const [carrier, setCarrier] = useState("");
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    const submitShip = async () => {
        if (!tracking.trim()) return;
        setBusy(true);
        try {
            await axios.post(`${API}/orders/${o.id}/ship`, {
                tracking_number: tracking.trim(),
                carrier: carrier.trim() || null,
            });
            setOpen(false);
            onShipped();
        } catch (e) {
            alert("Couldn't mark as shipped. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <TableRow className="border-white/5 hover:bg-white/[0.02]" data-testid={`order-row-${o.id}`}>
            <TableCell className="text-storm-mist text-xs whitespace-nowrap align-top">{fmtDate(o.created_at)}</TableCell>
            <TableCell className="text-white/90 align-top">
                <div className="font-medium">{o.customer_name || "—"}</div>
                {o.customer_email && (
                    <a href={`mailto:${o.customer_email}`} className="text-storm-mist hover:text-storm-bolt inline-flex items-center gap-1 text-xs">
                        <Mail className="w-3 h-3" /> {o.customer_email}
                    </a>
                )}
                {o.shipping_address && (
                    <div className="text-storm-mist text-xs mt-1 whitespace-pre-line">{o.shipping_address}</div>
                )}
            </TableCell>
            <TableCell className="text-storm-mist max-w-xs align-top">
                <div className="line-clamp-3 text-xs">
                    {(o.items || []).map((i) => `${i.qty}× ${i.title}`).join(", ")}
                </div>
            </TableCell>
            <TableCell className="text-white font-medium align-top">
                ${(o.total_cents / 100).toFixed(2)}
                {o.shipping_cents > 0 && (
                    <div className="text-storm-mist text-xs font-normal">incl. ${(o.shipping_cents / 100).toFixed(2)} ship</div>
                )}
            </TableCell>
            <TableCell className="align-top">
                {o.status === "shipped" ? (
                    <div>
                        <span className="text-green-400 text-xs uppercase tracking-wider inline-flex items-center gap-1">
                            <Package className="w-3.5 h-3.5" /> Shipped
                        </span>
                        <div className="text-storm-mist text-xs mt-1">
                            {o.carrier ? `${o.carrier}: ` : ""}<span className="text-storm-bolt">{o.tracking_number}</span>
                        </div>
                    </div>
                ) : o.status === "paid" ? (
                    open ? (
                        <div className="space-y-2 min-w-[180px]">
                            <input
                                value={tracking}
                                onChange={(e) => setTracking(e.target.value)}
                                placeholder="Tracking number"
                                data-testid={`ship-tracking-${o.id}`}
                                className="w-full bg-storm-void border border-white/10 text-white text-xs rounded-none h-8 px-2 focus:outline-none focus:border-storm-bolt"
                            />
                            <input
                                value={carrier}
                                onChange={(e) => setCarrier(e.target.value)}
                                placeholder="Carrier (USPS, UPS…)"
                                className="w-full bg-storm-void border border-white/10 text-white text-xs rounded-none h-8 px-2 focus:outline-none focus:border-storm-bolt"
                            />
                            <div className="flex gap-2">
                                <button onClick={submitShip} disabled={busy || !tracking.trim()} data-testid={`ship-confirm-${o.id}`} className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50">
                                    {busy ? "Sending…" : "Confirm + Email"}
                                </button>
                                <button onClick={() => setOpen(false)} className="text-storm-mist text-xs hover:text-white">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => setOpen(true)} data-testid={`ship-btn-${o.id}`} className="text-xs uppercase tracking-wider border border-storm-bolt text-storm-bolt px-3 py-1.5 hover:bg-storm-bolt hover:text-black transition-colors">
                            Mark Shipped
                        </button>
                    )
                ) : (
                    <span className="text-storm-mist text-xs uppercase tracking-wider">Pending</span>
                )}
            </TableCell>
        </TableRow>
    );
};

const StatBlock = ({ icon, label, value, testid }) => (
    <div className="surface-solid p-6 flex items-center gap-5" data-testid={testid}>
        <span className="inline-flex items-center justify-center w-11 h-11 bg-storm-bolt text-black">
            {icon}
        </span>
        <div>
            <div className="text-xs uppercase tracking-[0.25em] text-storm-mist">{label}</div>
            <div className="font-heading text-4xl text-white mt-1 leading-none">{value}</div>
        </div>
    </div>
);

const TrafficPanel = ({ traffic }) => {
    const chartData = (traffic.last_7_days || []).map((d) => ({
        day: new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" }),
        views: d.views,
    }));

    return (
        <div className="surface-solid p-6 md:p-7" data-testid="traffic-panel">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-storm-bolt text-black">
                        <TrendingUp className="w-4.5 h-4.5" />
                    </span>
                    <div>
                        <div className="font-heading text-lg text-white uppercase tracking-wide">Traffic</div>
                        <div className="text-xs text-storm-mist">Self-hosted page views — nothing shared with anyone else.</div>
                    </div>
                </div>
                <div className="flex gap-8">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-storm-mist">All-time</div>
                        <div className="font-heading text-2xl text-white">{traffic.total_views}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-storm-mist">Last 7 days</div>
                        <div className="font-heading text-2xl text-storm-bolt">{traffic.views_last_7_days}</div>
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
                <div className="md:col-span-2 h-48">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                            <XAxis dataKey="day" stroke="#8a93a8" fontSize={11} tickLine={false} axisLine={false} />
                            <YAxis stroke="#8a93a8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip
                                contentStyle={{ background: "#0B0C15", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0 }}
                                labelStyle={{ color: "#fff" }}
                                cursor={{ fill: "rgba(255,232,0,0.08)" }}
                            />
                            <Bar dataKey="views" fill="#FFE800" radius={[2, 2, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-storm-mist mb-3">Top Pages (7d)</div>
                    {(traffic.top_pages || []).length === 0 ? (
                        <p className="text-storm-mist text-sm">No views yet.</p>
                    ) : (
                        <ul className="space-y-2">
                            {traffic.top_pages.map((p) => (
                                <li key={p.path} className="flex items-center justify-between gap-3 text-sm">
                                    <span className="text-white/85 font-mono text-xs truncate">{p.path}</span>
                                    <span className="text-storm-bolt shrink-0">{p.views}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ---- Promo Codes Panel ---- */
const PromoCodesPanel = ({ promoCodes, reload }) => {
    const [code, setCode] = useState("");
    const [minQty, setMinQty] = useState("");
    const [minSpend, setMinSpend] = useState("");
    const [freeShipping, setFreeShipping] = useState(false);
    const [percentOff, setPercentOff] = useState("");
    const [fixedOff, setFixedOff] = useState("");
    const [freeItems, setFreeItems] = useState("");
    const [maxUses, setMaxUses] = useState("");
    const [busy, setBusy] = useState(false);

    const fmtDollars = (cents) => `$${(cents / 100).toFixed(2)}`;

    const rewardSummary = (pc) => {
        const parts = [];
        if (pc.free_shipping) parts.push("Free shipping");
        if (pc.percent_off > 0) parts.push(`${pc.percent_off}% off`);
        if (pc.fixed_off_cents > 0) parts.push(`${fmtDollars(pc.fixed_off_cents)} off`);
        if (pc.free_items > 0) parts.push(`${pc.free_items} free item${pc.free_items > 1 ? "s" : ""}`);
        return parts.join(" + ") || "No reward";
    };

    const conditionSummary = (pc) => {
        const parts = [];
        if (pc.min_qty > 0) parts.push(`min ${pc.min_qty} items`);
        if (pc.min_spend_cents > 0) parts.push(`min ${fmtDollars(pc.min_spend_cents)}`);
        return parts.length ? parts.join(", ") : "No minimum";
    };

    const create = async () => {
        if (!code.trim()) { alert("Enter a code."); return; }
        const hasReward = freeShipping || percentOff || fixedOff || freeItems;
        if (!hasReward) { alert("Set at least one reward."); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/promo-codes`, {
                code: code.trim(),
                min_qty: parseInt(minQty || "0", 10),
                min_spend_cents: Math.round(parseFloat(minSpend || "0") * 100),
                free_shipping: freeShipping,
                percent_off: parseInt(percentOff || "0", 10),
                fixed_off_cents: Math.round(parseFloat(fixedOff || "0") * 100),
                free_items: parseInt(freeItems || "0", 10),
                max_uses: maxUses ? parseInt(maxUses, 10) : null,
            });
            setCode(""); setMinQty(""); setMinSpend(""); setFreeShipping(false);
            setPercentOff(""); setFixedOff(""); setFreeItems(""); setMaxUses("");
            await reload();
        } catch (e) {
            alert(e?.response?.data?.detail || "Couldn't create code.");
        } finally { setBusy(false); }
    };

    const toggle = async (pc) => {
        await axios.post(`${API}/promo-codes/${pc.id}/toggle`, null, { params: { active: !pc.active } });
        reload();
    };

    const remove = async (pc) => {
        if (!window.confirm(`Delete code "${pc.code}" permanently?`)) return;
        await axios.delete(`${API}/promo-codes/${pc.id}`);
        reload();
    };

    return (
        <div className="grid lg:grid-cols-2 gap-10">
            <div>
                <h3 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-4">Build a Promo Code</h3>
                <div className="space-y-5 max-w-md">
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">Code (what the customer types)</label>
                        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. POKEFEST"
                            className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-storm-bolt uppercase" />
                    </div>
                    <div className="border border-white/10 p-4 space-y-3">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-storm-bolt">Conditions (optional)</div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] uppercase text-storm-mist block mb-1">Min items in cart</label>
                                <input value={minQty} onChange={(e) => setMinQty(e.target.value)} inputMode="numeric" placeholder="0"
                                    className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase text-storm-mist block mb-1">Min spend ($)</label>
                                <input value={minSpend} onChange={(e) => setMinSpend(e.target.value)} inputMode="decimal" placeholder="0.00"
                                    className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt" />
                            </div>
                        </div>
                    </div>
                    <div className="border border-white/10 p-4 space-y-3">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-storm-bolt">Rewards (pick any combo)</div>
                        <button onClick={() => setFreeShipping(!freeShipping)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 border text-sm text-left ${freeShipping ? "border-storm-bolt/50 text-white" : "border-white/10 text-storm-mist/50"}`}>
                            <span className={`w-3 h-3 rounded-full ${freeShipping ? "bg-storm-bolt" : "bg-white/10"}`} />
                            Free shipping
                        </button>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="text-[10px] uppercase text-storm-mist block mb-1">% off</label>
                                <input value={percentOff} onChange={(e) => setPercentOff(e.target.value)} inputMode="numeric" placeholder="0"
                                    className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase text-storm-mist block mb-1">$ off</label>
                                <input value={fixedOff} onChange={(e) => setFixedOff(e.target.value)} inputMode="decimal" placeholder="0.00"
                                    className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase text-storm-mist block mb-1">Free items</label>
                                <input value={freeItems} onChange={(e) => setFreeItems(e.target.value)} inputMode="numeric" placeholder="0"
                                    className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt" />
                            </div>
                        </div>
                        <p className="text-storm-mist/50 text-[10px]">Free items removes the cheapest item(s) in the cart.</p>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">Max uses (blank = unlimited)</label>
                        <input value={maxUses} onChange={(e) => setMaxUses(e.target.value)} inputMode="numeric" placeholder="Unlimited"
                            className="w-full bg-storm-void border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-storm-bolt" />
                    </div>
                    <button onClick={create} disabled={busy} className="btn-primary disabled:opacity-50">
                        {busy ? "Creating…" : "Create code"}
                    </button>
                </div>
            </div>
            <div>
                <h3 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-4">Your Codes ({promoCodes.length})</h3>
                <div className="space-y-3">
                    {promoCodes.length === 0 && <p className="text-storm-mist text-sm">No promo codes yet.</p>}
                    {promoCodes.map((pc) => (
                        <div key={pc.id} className={`surface-solid p-4 flex items-center gap-4 ${!pc.active ? "opacity-50" : ""}`}>
                            <Tag className="w-5 h-5 text-storm-bolt shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-white font-heading uppercase tracking-wider">{pc.code}</div>
                                <div className="text-storm-bolt text-xs">{rewardSummary(pc)}</div>
                                <div className="text-storm-mist text-[10px]">{conditionSummary(pc)} · {pc.uses} use{pc.uses !== 1 ? "s" : ""}{pc.max_uses ? ` / ${pc.max_uses} max` : ""}</div>
                            </div>
                            <button onClick={() => toggle(pc)}
                                className={`text-xs font-heading uppercase px-3 py-1.5 border ${pc.active ? "border-storm-bolt/30 text-storm-bolt" : "border-white/10 text-storm-mist"}`}>
                                {pc.active ? "Active" : "Off"}
                            </button>
                            <button onClick={() => remove(pc)} className="text-storm-mist hover:text-red-400 p-1">
                                <span className="text-xs">✕</span>
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AdminPage;
